/**
 * Shared language code normalization helpers.
 *
 * Why this exists:
 * - We support some locale folders that are NOT all-lowercase (e.g. 'ht_HT', 'zh_CN').
 * - i18next can lower-case language codes when `lowerCaseLng: true` (e.g. 'zh_CN' -> 'zh_cn', 'ht_HT' -> 'ht_ht').
 * - Different sources may persist slightly different variants (hyphen vs underscore, casing).
 *
 * This file centralizes "alias -> canonical" mapping so Profile + Needs Analysis behave consistently.
 */

/**
 * Map of known language-code aliases to the canonical supported language code used in UI/settings.
 */
export const LANGUAGE_ALIASES_TO_SUPPORTED: Record<string, string> = {
  // Tagalog / Filipino
  fil: 'tl',
  'fil-ph': 'tl',
  fil_ph: 'tl',
  'tl-ph': 'tl',
  tl_ph: 'tl',
  // Haitian Creole
  ht_ht: 'ht_HT',
  'ht-ht': 'ht_HT',
  // Simplified Chinese
  'zh-cn': 'zh_CN',
  'zh-CN': 'zh_CN',
  zh_cn: 'zh_CN',
  // Pashto (Afghanistan)
  ps_af: 'ps_AF',
  'ps-af': 'ps_AF',
  // Lao (Laos)
  lo_la: 'lo_LA',
  'lo-la': 'lo_LA',
}

export function normalizeToSupportedLanguageCode(code: string | null | undefined): string | undefined {
  if (!code) return undefined
  const trimmed = code.trim()
  return LANGUAGE_ALIASES_TO_SUPPORTED[trimmed] ?? trimmed
}

/**
 * Map i18next "detected/current" language codes to the correct locale folder.
 * This is specifically for loading `/public/locales/<folder>/<namespace>.json`.
 */
export const I18NEXT_LANGUAGE_TO_LOCALE_FOLDER: Record<string, string> = {
  // Tagalog / Filipino: browsers often report "fil" (Filipino), but our folder is "tl"
  fil: 'tl',
  'fil-ph': 'tl',
  fil_ph: 'tl',
  'tl-ph': 'tl',
  tl_ph: 'tl',
  // Haitian Creole: i18next might request ht_ht, but folder is ht_HT
  ht_ht: 'ht_HT',
  // Simplified Chinese: i18next might request zh-cn / zh_cn; our canonical folder is zh_CN
  'zh-cn': 'zh_CN',
  // i18next lowercases language codes when lowerCaseLng=true
  zh_cn: 'zh_CN',
  // Pashto: i18next may lowercase / hyphenate
  ps_af: 'ps_AF',
  'ps-af': 'ps_AF',
  // Lao: i18next may lowercase / hyphenate
  lo_la: 'lo_LA',
  'lo-la': 'lo_LA',
}

export function getLocaleFolderForI18nextLanguage(lng: string): string {
  return I18NEXT_LANGUAGE_TO_LOCALE_FOLDER[lng] ?? lng
}

/**
 * App locales whose text no ISO 639-1 code describes. Our Farsi catalog is
 * Latin-script (Finglish), so `fa` would point a third party at Arabic-script
 * content the learner didn't choose; Chuukese has no 639-1 code at all.
 */
const LOCALES_WITHOUT_ISO_639_1 = new Set(['fa-latn-ir', 'chk'])

/**
 * Reduce an app/i18next locale (e.g. 'zh_CN', 'ht_ht', 'es-mx') to the bare
 * ISO 639-1 language code most third parties key on (e.g. 'zh', 'ht', 'es').
 * Falls back to 'en', which every integration can serve.
 */
export function toIso6391LanguageCode(lng: string | null | undefined): string {
  const normalized = lng?.trim().toLowerCase()
  if (!normalized || LOCALES_WITHOUT_ISO_639_1.has(normalized)) return 'en'

  // Canonicalize aliases first ('fil-ph' -> 'tl', 'zh_cn' -> 'zh_CN') so the
  // alias table stays the single source for them.
  const primary = (normalizeToSupportedLanguageCode(normalized) ?? normalized).toLowerCase().split(/[-_]/)[0]
  return /^[a-z]{2}$/.test(primary) ? primary : 'en'
}
